import { useRef } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';

interface Props {
  /** URL de la photo (typiquement `/images/marketing/0R9A....webp`). */
  photo: string;
  /** Citation courte affichée par-dessus, en italique doré. */
  quote: string;
  /** Attribution optionnelle sous la citation (ex: nom du producteur). */
  attribution?: string;
  /** Position de cadrage CSS (par défaut 'center'). */
  objectPosition?: string;
}

/**
 * Bandeau full-bleed photo + citation italique, à intercaler dans une
 * page longue (ex: /menu entre 2 sections) pour aérer le scroll.
 *
 * Effet parallax léger sur desktop, statique en mobile / reduced-motion.
 */
export default function MenuImageBreak({
  photo,
  quote,
  attribution,
  objectPosition = 'center',
}: Props) {
  const ref = useRef<HTMLElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const disableScrollFx = prefersReducedMotion || isMobile;

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    disableScrollFx ? ['0%', '0%'] : ['-8%', '8%']
  );

  return (
    <section
      ref={ref}
      className="relative h-[40vh] min-h-[280px] md:min-h-[360px] overflow-hidden border-y border-[#1F1F1F]"
    >
      <motion.img
        src={photo}
        alt=""
        loading="lazy"
        style={{ y, objectPosition }}
        className="absolute inset-0 h-[116%] -top-[8%] w-full object-cover will-change-transform"
      />
      <div className="absolute inset-0 bg-[#0A0A0A]/70" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/30 via-transparent to-[#0A0A0A]/50" />

      <div className="relative h-full max-w-4xl mx-auto px-5 lg:px-8 flex flex-col items-center justify-center text-center">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-display italic text-2xl md:text-4xl text-[#D4A574] leading-snug drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]"
        >
          « {quote} »
        </motion.p>
        {attribution && (
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-4 text-[10px] uppercase tracking-[0.3em] text-neutral-300"
          >
            — {attribution}
          </motion.p>
        )}
      </div>
    </section>
  );
}
